import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, FormLabel, Heading, Input, Stack, useColorModeValue, useToast, Image, Spinner, Switch } from '@chakra-ui/react';
import { getBackendAssetURL, getBackendURL, apiFetch } from '../../utils/utils';

function SettingsPage() {
    const [razorpayTestKey, setRazorpayTestKey] = useState('');
    const [razorpayTestSecret, setRazorpayTestSecret] = useState('');
    const [razorpayLiveKey, setRazorpayLiveKey] = useState('');
    const [razorpayLiveSecret, setRazorpayLiveSecret] = useState('');
    const [razorpayWebhookSecret, setRazorpayWebhookSecret] = useState('');
    const [razorpayMode, setRazorpayMode] = useState('TEST');
    const [paymentLinkExpiryMinutes, setPaymentLinkExpiryMinutes] = useState(15);
    const [emailHost, setEmailHost] = useState('');
    const [emailUser, setEmailUser] = useState('');
    const [emailPass, setEmailPass] = useState('');
    const [emailPort, setEmailPort] = useState(587);
    const [emailSecure, setEmailSecure] = useState(false);
    const [qrCodeImg, setQrCodeImg] = useState(null);
    const [qrCodePath, setQrCodePath] = useState('');
    const [shinigamiBaseUrl, setShinigamiBaseUrl] = useState('');
    const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState(true);
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const toast = useToast();
    const bg = useColorModeValue('white', 'gray.800');

    useEffect(() => {
        apiFetch(`${getBackendURL()}/admin/settings`)
            .then(res => res.json())
            .then(data => {
                setRazorpayTestKey(data.razorpayTestKey || '');
                setRazorpayTestSecret(data.razorpayTestSecret || '');
                setRazorpayLiveKey(data.razorpayLiveKey || '');
                setRazorpayLiveSecret(data.razorpayLiveSecret || '');
                setRazorpayWebhookSecret(data.razorpayWebhookSecret || '');
                setRazorpayMode(data.razorpayMode || 'TEST');
                setPaymentLinkExpiryMinutes(data.paymentLinkExpiryMinutes || 15);
                setEmailHost(data.emailHost || '');
                setEmailUser(data.emailUser || '');
                setEmailPass(data.emailPass || '');
                setEmailPort(data.emailPort !== undefined ? data.emailPort : 587);
                setEmailSecure(data.emailSecure || false);
                setQrCodePath(data.qrCodePath || '');
                setShinigamiBaseUrl(data.shinigamiBaseUrl || '');
                setPaymentGatewayEnabled(data.paymentGatewayEnabled !== false);
                setEmailNotificationsEnabled(data.emailNotificationsEnabled !== false);
                if (data.qrCodePath) {
                    setQrCodeImg(data.qrCodePath.startsWith('http') ? data.qrCodePath : `${getBackendAssetURL()}/${data.qrCodePath}`);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching settings:', err);
                setLoading(false);
            });
    }, []);

    const handleSave = () => {
        setSaving(true);
        apiFetch(`${getBackendURL()}/admin/settings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                razorpayTestKey,
                razorpayTestSecret,
                razorpayLiveKey,
                razorpayLiveSecret,
                razorpayWebhookSecret,
                razorpayMode,
                paymentLinkExpiryMinutes,
                emailHost,
                emailUser,
                emailPass,
                emailPort,
                emailSecure,
                qrCodePath,
                shinigamiBaseUrl,
                paymentGatewayEnabled,
                emailNotificationsEnabled
            }),
        })
            .then(res => res.json())
            .then(data => {
                setSaving(false);
                if (data.success) {
                    toast({
                        title: 'Settings saved.',
                        description: "Settings have been successfully updated.",
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                } else {
                    toast({
                        title: 'Error saving settings.',
                        description: data.message,
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                }
            })
            .catch(err => {
                setSaving(false);
                console.error('Error saving settings:', err);
                toast({
                    title: 'Error.',
                    description: "Failed to connect to server.",
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            });
    };

    const handleImageUpload = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const formData = new FormData();
            formData.append('file', file);

            setUploading(true);
            apiFetch(`${getBackendURL()}/admin/settings/upload-qr`, {
                method: 'POST',
                body: formData,
            })
                .then(res => res.json())
                .then(data => {
                    setUploading(false);
                    if (data.success) {
                        setQrCodePath(data.filename);
                        setQrCodeImg(data.filename.startsWith('http') ? data.filename : `${getBackendAssetURL()}/${data.filename}`);
                        toast({
                            title: 'QR Code uploaded.',
                            description: "Image uploaded, please save settings to persist.",
                            status: 'info',
                            duration: 3000,
                        });
                    } else {
                        toast({
                            title: 'Upload failed.',
                            description: data.message,
                            status: 'error',
                            duration: 3000,
                        });
                    }
                })
                .catch(err => {
                    setUploading(false);
                    console.error('Error uploading QR code:', err);
                });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Box p={8} bg={bg} rounded="md" shadow="sm">
            <Heading size="md" mb={6}>System Settings</Heading>
            <Stack spacing={4}>
                <Box border="1px solid" borderColor="gray.200" p={4} rounded="md">
                    <Heading size="sm" mb={4}>Razorpay Credentials</Heading>

                    <FormControl display="flex" alignItems="center" mb={4}>
                        <FormLabel htmlFor="payment-gateway-switch" mb="0" mr={4}>
                            Enable Online Payment Gateway
                        </FormLabel>
                        <Switch
                            id="payment-gateway-switch"
                            isChecked={paymentGatewayEnabled}
                            onChange={(e) => setPaymentGatewayEnabled(e.target.checked)}
                        />
                    </FormControl>

                    <FormControl mb={4}>
                        <FormLabel>Razorpay Mode</FormLabel>
                        <Stack direction="row" spacing={4}>
                            <Button
                                size="sm"
                                colorScheme={razorpayMode === 'TEST' ? 'yellow' : 'gray'}
                                onClick={() => setRazorpayMode('TEST')}
                            >
                                TEST Mode
                            </Button>
                            <Button
                                size="sm"
                                colorScheme={razorpayMode === 'LIVE' ? 'green' : 'gray'}
                                onClick={() => setRazorpayMode('LIVE')}
                            >
                                LIVE Mode
                            </Button>
                        </Stack>
                    </FormControl>

                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4} mb={4}>
                        <Box flex={1} p={3} border="1px dashed" borderColor="yellow.300" bg="yellow.50" rounded="md">
                            <Heading size="xs" mb={3} color="yellow.700">Test Keys</Heading>
                            <FormControl mb={2}>
                                <FormLabel fontSize="xs">Test Key ID</FormLabel>
                                <Input size="sm" value={razorpayTestKey} onChange={e => setRazorpayTestKey(e.target.value)} placeholder="rzp_test_..." />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs">Test Key Secret</FormLabel>
                                <Input size="sm" type="password" value={razorpayTestSecret} onChange={e => setRazorpayTestSecret(e.target.value)} />
                            </FormControl>
                        </Box>

                        <Box flex={1} p={3} border="1px dashed" borderColor="green.300" bg="green.50" rounded="md">
                            <Heading size="xs" mb={3} color="green.700">Live Keys</Heading>
                            <FormControl mb={2}>
                                <FormLabel fontSize="xs">Live Key ID</FormLabel>
                                <Input size="sm" value={razorpayLiveKey} onChange={e => setRazorpayLiveKey(e.target.value)} placeholder="rzp_live_..." />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs">Live Key Secret</FormLabel>
                                <Input size="sm" type="password" value={razorpayLiveSecret} onChange={e => setRazorpayLiveSecret(e.target.value)} />
                            </FormControl>
                        </Box>
                    </Stack>

                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                        <FormControl>
                            <FormLabel fontSize="xs">Webhook Secret</FormLabel>
                            <Input size="sm" value={razorpayWebhookSecret} onChange={e => setRazorpayWebhookSecret(e.target.value)} placeholder="Secret for signature verification" />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs">Payment Link Expiry (Minutes)</FormLabel>
                            <Input size="sm" type="number" value={paymentLinkExpiryMinutes} onChange={e => setPaymentLinkExpiryMinutes(parseInt(e.target.value))} />
                        </FormControl>
                    </Stack>
                </Box>

                <Box border="1px solid" borderColor="gray.200" p={4} rounded="md">
                    <Heading size="sm" mb={4}>Email Settings (Resend)</Heading>
                    <FormControl display="flex" alignItems="center" mb={4}>
                        <FormLabel htmlFor="email-notifications-switch" mb="0" mr={4}>
                            Send Payment Link Emails
                        </FormLabel>
                        <Switch
                            id="email-notifications-switch"
                            isChecked={emailNotificationsEnabled}
                            onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
                        />
                    </FormControl>
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4} mb={3}>
                        <FormControl flex={1}>
                            <FormLabel>Sender Email Address</FormLabel>
                            <Input value={emailUser} onChange={e => setEmailUser(e.target.value)} placeholder="noreply@yourdomain.com" />
                        </FormControl>
                        <FormControl flex={2}>
                            <FormLabel>Resend API Key</FormLabel>
                            <Input type="password" value={emailPass} onChange={e => setEmailPass(e.target.value)} placeholder="re_..." />
                        </FormControl>
                    </Stack>
                </Box>

                <Box border="1px solid" borderColor="gray.200" p={4} rounded="md">
                    <Heading size="sm" mb={4}>Landing Page Redirection</Heading>
                    <FormControl>
                        <FormLabel>Landing Page Base URL (for Payment Redirection)</FormLabel>
                        <Input value={shinigamiBaseUrl} onChange={e => setShinigamiBaseUrl(e.target.value)} placeholder="https://digigoindia.com" />
                    </FormControl>
                </Box>

                <Box border="1px solid" borderColor="gray.200" p={4} rounded="md">
                    <Heading size="sm" mb={4}>Payment QR Code</Heading>
                    <FormControl>
                        <FormLabel>Upload QR Code Image (Offline Flow)</FormLabel>
                        <Input type="file" accept="image/*" onChange={handleImageUpload} p={1} isDisabled={uploading} />
                        {uploading && <Spinner size="sm" mt={2} />}
                        {qrCodeImg && <Image mt={4} src={qrCodeImg} alt="QR Code Preview" maxW="200px" border="1px solid" borderColor="gray.200" borderRadius="md" />}
                    </FormControl>
                </Box>

                <Button colorScheme="blue" onClick={handleSave} alignSelf="flex-start" isLoading={saving} size="lg" w="full">Save All Settings</Button>
            </Stack>
        </Box>
    );
}

export default SettingsPage;
